import L from "leaflet";
import React, {useEffect, useMemo, useRef, useState} from "react";
import * as Const from "../Const";
import ReactDOMServer from "react-dom/server";
import {NavigationSkills} from "./NavigationSkills";
import {SHOW_UNFINISHED_STUFF} from "../Const";

const machineIcon = L.icon({
    iconUrl: 'assets/icons/assistant_navigation.png',
    iconSize: [28, 28],
    iconAnchor: [14, 14],
});

const machineNoHeadingIcon = L.icon({
    iconUrl: 'assets/icons/assistant_navigation_no_heading.png',
    iconSize: [26, 26],
    iconAnchor: [13, 13],
});

const machineOfflineIcon = L.icon({
    iconUrl: 'assets/icons/assistant_navigation_offline.png',
    iconSize: [20, 20],
    iconAnchor: [10, 10],
});

const TAG = "MAP-MM";


function OverviewMapMachineMarkers( {mapReference, machines, livePositions, velocities, navigationSkills, workConfigurations,
                            selectedMachineName, onMachineClicked, children} ) {

    const mapRef = useMemo(() => {
        console.log(TAG, "map ref changed");
        return mapReference;
    }, [mapReference]);
    
    const markerRefs = useRef([]);
    const savedMachinePopupContent = useRef(null);

    const [savedSelectedMachineName, setSavedSelectedMachineName] = useState(null);
    const [updates, setUpdates] = useState(0);

    useEffect(() => {
        setUpdates(updates => updates + 1);
    }, [machines, livePositions, velocities, navigationSkills, workConfigurations, selectedMachineName]);


    const liveMachines = useMemo(() => {
        return machines ? machines.filter((machine) => Object.keys(livePositions).find(value => value === machine.name)) : null;
    }, [livePositions, machines]);


    const machineMarkerData = useMemo(() => {
        // console.log("MAPv3", "update machine marker data");
        if (liveMachines && livePositions && mapRef.current) {
            return liveMachines.map(m => {
                const livePos = livePositions[m.name];
                m.id = m.name;
                m.lat = livePos.latitude;
                m.lng = livePos.longitude;
                m.heading = m.isOnline ? (livePos.headingQuality > 0 ? -livePos.heading : 0) : 0;
                m.tooltip = m.name;
                m.icon = m.isOnline ? (livePos.headingQuality > 0 ? machineIcon : machineNoHeadingIcon) : machineOfflineIcon;

                // console.log("MAPv3 marker data", m.name, m);
                return m;
            })
        } else {
            return [];
        }
    }, [mapRef, liveMachines, livePositions]);



    useEffect(() => {
        // console.log("MAPv3", "update machine markers");
        machineMarkerData.forEach(marker => {

            if (marker.lat && marker.lng) {
                if (!markerRefs.current[marker.id]) {
                    // console.log("MAPv3", "adding marker for", marker.id, marker);
                    const newMarker = L.marker([marker.lat, marker.lng], {
                        pane: Const.PANE_NAME_MACHINES,
                        icon: machineNoHeadingIcon,
                        rotationOrigin: 'center',
                        rotationAngle: marker.heading
                    });
                    newMarker.addTo(mapRef.current);
                    newMarker.bindTooltip(marker.tooltip, { direction: "top", offset: [0, -15], closeOnEscapeKey: false, closeOnClick: false, autoPan: false });
                    newMarker.on('click', () => onMachineClicked(marker.id));
                    markerRefs.current[marker.id] = newMarker;

                } else {
                    // console.log("MAPv3", "updating marker", marker.id, marker);
                    const currentMarker = markerRefs.current[marker.id];
                    currentMarker.setLatLng([marker.lat ?? 0, marker.lng ?? 0]);
                    currentMarker.setTooltipContent(marker.tooltip);
                    currentMarker.setIcon(marker.icon);
                    currentMarker.options.rotationAngle = marker.heading;
                    currentMarker.off('click');
                    currentMarker.on('click', () => onMachineClicked(marker.id));
                }
            }
        });

        const newMarkerIds = machineMarkerData.map(marker => marker.id);
        Object.keys(markerRefs.current).forEach(id => {
            if (!newMarkerIds.includes(id)) {
                markerRefs.current[id].remove();
                delete markerRefs.current[id];
            }
        });
    }, [machineMarkerData, mapRef, onMachineClicked]);


    useEffect(() => {
        if (selectedMachineName !== savedSelectedMachineName) {
            // console.log("MAPv3: selected machine changed", selectedMachineName);

            if (savedSelectedMachineName) {
                console.log(TAG, "removing popup for", savedSelectedMachineName);
                markerRefs.current[savedSelectedMachineName]?.closePopup();
                markerRefs.current[savedSelectedMachineName]?.unbindPopup();
            }

            if (selectedMachineName) {
                if (markerRefs.current[selectedMachineName]) {
                    console.log(TAG, "adding popup for", selectedMachineName);

                    const element = (
                        <div id={"popup_" + selectedMachineName} style={{marginTop: "0em"}}>
                            <div className={"text-nowrap"}>{selectedMachineName}</div>
                        </div>
                    );

                    const content = ReactDOMServer.renderToString(element);

                    markerRefs.current[selectedMachineName]
                        .bindPopup(content, {offset: [0, -2], autoClose: false, closeOnEscapeKey: false, closeButton: false, closeOnClick: false, autoPan: false, myId: selectedMachineName})
                        .openPopup();

                    // document.getElementById("close_" + selectedMachineName)?.addEventListener('click', (e) => {
                    //     console.log("MAPv3 closing popup for " + selectedMachineName);
                    //     onMachineClicked(selectedMachineName);
                    // });
                }
            }
            setSavedSelectedMachineName(selectedMachineName);
        }
    }, [navigationSkills, onMachineClicked, savedSelectedMachineName, selectedMachineName]);



    useEffect(() => {
        // const popupContainer = document.getElementById("popup_" + selectedMachineName);
        if (selectedMachineName && navigationSkills[selectedMachineName]) {
            // console.log("MAPv3", "Popup", selectedMachineName);
            const element = (
                <>
                    <div className={"text-nowrap"}><strong>{selectedMachineName}</strong></div>
                    <NavigationSkills navigationSkill={navigationSkills[selectedMachineName]}/>
                    {livePositions[selectedMachineName]
                        ? <div>{livePositions[selectedMachineName].positionFix}</div>
                        : null
                    }
                    {/*<div>{livePositions[selectedMachineName].heading}</div>*/}
                    {velocities[selectedMachineName]
                        ? <div>{velocities[selectedMachineName]} km/h</div>
                        : null
                    }
                </>
            );
            const content = ReactDOMServer.renderToString(element);
            if (content !== savedMachinePopupContent.current) {
                // console.log("MAPv3", "Popup", selectedMachineName, "updating");
                savedMachinePopupContent.current = content;

                markerRefs.current[selectedMachineName]?.closePopup();
                markerRefs.current[selectedMachineName]?.unbindPopup();

                if (markerRefs.current[selectedMachineName]) {
                    markerRefs.current[selectedMachineName]
                        .bindPopup(content, {offset: [0, -2], autoClose: false, closeOnEscapeKey: false, closeButton: false, closeOnClick: false, autoPan: false, myId: selectedMachineName})
                        .openPopup();
                }
            }
        }
        else {
            savedMachinePopupContent.current = null;
        }
    }, [livePositions, navigationSkills, selectedMachineName, velocities]);


    return (
        <>
            {SHOW_UNFINISHED_STUFF ?
                <div className={"position-absolute start-50 bottom-0 m-2 bg-light"} style={{zIndex: Const.PANEL_OVERLAY_Z + 20}}>[{TAG} {updates}]</div>
                    : null
            }
        </>
    );
}


export default OverviewMapMachineMarkers;
